<template>
  <v-row no-gutters>
    <v-col cols="12">
      <form>
        <v-row no-gutters>
          <v-col cols="12" sm="6" md="4">
            <TextField
              v-model.trim="form.email"
              rules="required|email_without_polish|max:255"
              name="email"
              autocomplete="email"
              inputmode="email"
              :label="$t('label.email')"
              required
            />
            <TextField
              v-model="form.phone_number"
              v-mask="phoneMask"
              rules="required|phoneNumber"
              :label="$t('label.phoneNumber')"
              name="phone"
              autocomplete="tel"
              inputmode="tel"
              required
              type="tel"
            />
            <TextField
              v-model="form.first_name"
              rules="required|name|max:255"
              :name="$t('label.firstName')"
              autocomplete="given-name"
              :label="$t('label.firstName')"
              required
            />
            <TextField
              v-model="form.last_name"
              rules="required|name|max:255"
              :name="$t('label.lastName')"
              autocomplete="family-name"
              :label="$t('label.lastName')"
              required
            />
            <TextArea
              v-model="form.comment"
              name="comment"
              rules="regularField"
              :label="$t('label.formAndTime')"
              :placeholder="$t('label.type')"
              auto-grow
              rows="1"
              flat
            />
          </v-col>
        </v-row>

        <RadioGroup
          v-model="form.category"
          :labels="groupLabels"
          name="category"
          :label="$t('label.whatGroup')"
          rules="required"
          required
          class="mt-0"
          label-font-weight="medium"
        />

        <v-row no-gutters v-if="enableGiftGiverNameField">
          <v-col cols="12">
            <p v-orphans class="f-16 mb-n3 mt-0 pl-3 sz-primary-grey--text">
              {{ $t('label.giftGiverName') }}
            </p>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <TextField
              v-model="form.gift_giver_name"
              rules="regularField|max:180"
              name="giftGiverName"
            />
          </v-col>
        </v-row>

        <CRZ v-model="form.consents" :fields="consentConfig.fieldsets" />

        <CaptchaField
          v-if="captchaRequired"
          v-model="form.captchaToken"
          rules="required"
          name="captcha"
          required
        />

        <BaseButton
          id="swr-step3-form-submit"
          :disabled="getLoader"
          :loading="getLoader"
          class="mt-5"
          arrow
          :block="$vuetify.display.xs"
          @click="onSubmit"
        >
          {{ $t('family.confirmFamilyChoose') }}
        </BaseButton>
        <div class="crz-container mt-5">
          <ExpandableText
            v-for="(field, index) in crzTextFields"
            :key="index"
            :text="field.options.label"
            :additional-text="field.options.details"
          />
        </div>
      </form>
    </v-col>
    <ModalCustom v-model="isFamilyNotAvailable">
      <template #header>
        <p v-if="donorResponse.isFamilyChosen" class="f-24 mb-2 font-weight-medium">
          {{ $t('family.chosenFamily') }}
        </p>
        <p v-else class="f-24 mb-2 font-weight-medium">
          {{ $t('family.otherLocation') }}
        </p>
        <p v-if="donorResponse.isFamilyChosen" class="f-18 mb-0">
          {{ $t('family.chooseOther') }}
        </p>
        <BaseButton
          :to="{ name: returnLink.toName, params: returnLink.params }"
          block-mobile
          arrow
          class="mt-5 mb-3"
        >
          {{ $t('label.findOtherFamily') }}
        </BaseButton>
      </template>
      <template #footer>
        <p class="mb-0">
          {{ $t('family.donateForSzp') }}
        </p>
        <BaseButton :href="szpPaymentLink" variant="secondary" arrow class="mt-5 mb-3">
          {{ $t('label.donateForSzp') }}
        </BaseButton>
      </template>
    </ModalCustom>
  </v-row>
</template>
<script setup lang="ts">
import CaptchaField from '@/components/FamilyChoiceForm/CaptchaField.vue'
import BaseButton from '@/components/widgets/BaseButton.vue'
import CRZ from '@/components/widgets/CRZ.vue'
import RadioGroup from '@/components/widgets/Inputs/RadioGroup.vue'
import TextArea from '@/components/widgets/Inputs/TextArea.vue'
import TextField from '@/components/widgets/Inputs/TextField.vue'
import ModalCustom from '@/components/widgets/ModalCustom.vue'
import { LINKS } from '@/constants/externalLinks'
import {
  createFamilyChosenEvent,
  createFormErrorsEvent,
  createFormSubmitEvent
} from '@/plugins/gtm/events'
import { useConsentStore } from '@/stores/Consent'
import { useDonorStore } from '@/stores/Donor'
import type { PersonalDataForm, PersonalDataPreparedForBackend } from '@/types'
import { useGtm } from '@gtm-support/vue-gtm'
import axios from 'axios'
import { useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
// @ts-expect-error: vue-the-mask is not typed. Should be changed to maska library like in ap-frontend
import { mask } from 'vue-the-mask'
import { useGoTo } from 'vuetify'
// @ts-expect-error: Importing Vuex store
import { useStore } from 'vuex'
import ExpandableText from '@/components/widgets/ExpandableText.vue'
import { handleSentryError } from '@/utils/handleSentryError'
import type { GenericObject, InvalidSubmissionContext } from 'vee-validate'
import ROUTE_NAMES from '@/constants/routeNames'
import { PRIVATE_GIFT_GIVER_CATEGORY_ID } from '@/constants/giftGiverCategory.ts'
type Props = {
  warehouseId: number
  captchaRequired: boolean
}

const props = defineProps<Props>()

const goTo = useGoTo()
const gtm = useGtm()
// vuex
const store = useStore()
const router = useRouter()
const route = useRoute()
const vMask = mask

const donorStore = useDonorStore()

const phoneMask = {
  mask: 'FFF FFF FFF FFF FFF FFF FF',
  tokens: {
    F: {
      pattern: /\+|[0-9]/
    }
  }
}

const szpPaymentLink: string = LINKS.szpPaymentForm

const form = ref<PersonalDataForm>({
  email: '',
  phone_number: '',
  first_name: '',
  last_name: '',
  comment: '',
  category: null,
  gift_giver_name: '',
  consents: {},
  captchaToken: null
})

const enableGiftGiverNameField = computed(() => {
  return form.value.category !== null && form.value.category !== PRIVATE_GIFT_GIVER_CATEGORY_ID
})

const { handleSubmit } = useForm()

const prepareFormDataForSubmit = (form: PersonalDataForm): PersonalDataPreparedForBackend => {
  const { captchaToken, phone_number, ...rest } = form

  const formData: PersonalDataPreparedForBackend = {
    ...rest,
    ...(captchaToken && { 'g-recaptcha-response': captchaToken }),
    phone_number: phone_number?.replace(/\s+/g, '')
  }

  if (!enableGiftGiverNameField.value) {
    formData.gift_giver_name = ''
  }

  return formData
}

const onValidSubmit = async (): Promise<void> => {
  const preparedFormData: PersonalDataPreparedForBackend = prepareFormDataForSubmit(form.value)
  const familyEditionId: string | string[] = route.params.familyEditionId
  const warehouseId: number = props.warehouseId

  try {
    const donor = await store.dispatch('donor/postDonor', {
      familyEditionId,
      warehouseId,
      data: preparedFormData
    })

    donorStore.setDonor(donor)

    const gtmEventValue = {
      familyEditionId,
      warehouseId
    }
    if (!gtm) {
      handleSentryError(new Error('GTM not initialized'))
    } else {
      gtm.trackEvent(createFormSubmitEvent(gtmEventValue))
      gtm.trackEvent(createFamilyChosenEvent(gtmEventValue))
    }
    router.push({ name: ROUTE_NAMES.THANKS })
  } catch (error) {
    if (error instanceof Error) handleSentryError(error)
    const isAxiosError = axios.isAxiosError(error)
    if (isAxiosError && error.response?.status === 404) {
      donorResponse.value.isWarehouseValid = error.response.data.data.isWarehouseValid
      donorResponse.value.isFamilyChosen = error.response.data.data.isFamilyChoosen
    }
  }
}

const onInvalidSubmit = ({
  errors
}: InvalidSubmissionContext<GenericObject, GenericObject>): void => {
  if (!gtm) {
    handleSentryError(new Error('GTM not initialized'))
  } else {
    gtm.trackEvent(createFormErrorsEvent(errors))
  }
  const errorTextElement = document.querySelector<HTMLDivElement>('div.v-input--error')
  if (!errorTextElement) return
  goTo(errorTextElement, {
    easing: 'easeInQuad'
  })
}
const onSubmit = handleSubmit(onValidSubmit, onInvalidSubmit)

const isFamilyNotAvailable = ref<boolean>(false)

const donorResponse = ref({
  isWarehouseValid: true,
  isFamilyChosen: false
})

watch(
  donorResponse,
  (value) => {
    isFamilyNotAvailable.value = value.isWarehouseValid === false || value.isFamilyChosen === true
  },
  { deep: true }
)

const consentStore = useConsentStore()

const getLoader = computed(() => store.getters['donor/getLoader'])
const bubbleId = computed(() => store.getters['families/getBubbleId'])
const consentConfig = computed(() => consentStore.consent ?? { fieldsets: [] })
const getGiftgiverCategories = computed(() => consentStore.giftgiverCategories)
const groupLabels = computed(() =>
  getGiftgiverCategories.value?.map((giftGiverCategory) => ({
    label: giftGiverCategory?.description,
    value: giftGiverCategory?.id
  }))
)
const returnLink = computed(() => ({
  toName: bubbleId.value ? ROUTE_NAMES.FAMILIES_LIST : ROUTE_NAMES.HOME,
  params: bubbleId.value ? { bubbleId: bubbleId.value } : {}
}))
const crzTextFields = computed(() => consentStore.getSplittedLabelTextFields)
</script>
